import React, { useState, useEffect } from "react";
import axios from "axios";
import PasswordGate from "./PasswordGate";
import ConfigPanel from "./ConfigPanel";
import ScheduleManager from "./ScheduleManager";
import LogViewer from "./LogViewer";
import StatsPanel from "./StatsPanel";
import LogFileManager from "./LogFileManager";
import DominantConfigEditor from "./DominantConfigEditor";

const API_BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:3051";

interface MonitoringConfig {
  enabled: boolean;
  timezone: string;
  schedules: Schedule[];
  logRetentionDays: number;
}

interface Schedule {
  id: string;
  name: string;
  start_at: string;
  end_at: string;
  days: number[];
  enabled: boolean;
}

const DominantMonitoringAdmin: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [config, setConfig] = useState<MonitoringConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"config" | "logs" | "stats">(
    "stats"
  );

  // Check if password is stored in localStorage
  useEffect(() => {
    const storedPassword = localStorage.getItem("dominant_monitoring_password");
    if (storedPassword) {
      setPassword(storedPassword);
      verifyPassword(storedPassword);
    }
  }, []);

  const verifyPassword = async (pwd: string) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/api/dominant-monitoring/config`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-admin-password": pwd,
          },
        }
      );

      if (response.data.success) {
        setIsAuthenticated(true);
        setPassword(pwd);
        localStorage.setItem("dominant_monitoring_password", pwd);
        setConfig(response.data.config);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Authentication failed");
      setIsAuthenticated(false);
      localStorage.removeItem("dominant_monitoring_password");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = (pwd: string) => {
    verifyPassword(pwd);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword("");
    localStorage.removeItem("dominant_monitoring_password");
  };

  const refreshConfig = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/api/dominant-monitoring/config`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-admin-password": password,
          },
        }
      );

      if (response.data.success) {
        setConfig(response.data.config);
      }
    } catch (err: any) {
      console.error("Failed to refresh config:", err);
    }
  };

  if (!isAuthenticated) {
    return (
      <PasswordGate
        onSubmit={handlePasswordSubmit}
        loading={loading}
        error={error}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                🚨 Dominant Gateway Monitoring
              </h1>
              <p className="text-gray-600 mt-1">
                Monitor and analyze dominant gateway changes during abandoned
                periods
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab("stats")}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "stats"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                📊 Statistics
              </button>
              <button
                onClick={() => setActiveTab("logs")}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "logs"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                📋 Logs
              </button>
              <button
                onClick={() => setActiveTab("config")}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "config"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                ⚙️ Configuration
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {config && (
          <>
            {activeTab === "stats" && <StatsPanel password={password} />}

            {activeTab === "logs" && <LogViewer password={password} />}

            {activeTab === "config" && (
              <div className="space-y-6">
                <ConfigPanel
                  config={config}
                  password={password}
                  onConfigUpdate={refreshConfig}
                />
                <DominantConfigEditor password={password} />
                <ScheduleManager
                  schedules={config.schedules}
                  password={password}
                  onSchedulesUpdate={refreshConfig}
                />
                <LogFileManager password={password} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DominantMonitoringAdmin;
